import { forwardRef, useCallback, type ComponentProps, type ComponentRef } from 'react'
import { TextInput } from './TextInput'
import { formatIsoDateInput, formatSvDui, normalizeLegalEntityName } from '@/lib/utils/sv-identity'
import { formatSvNit, SV_NIT_FORMATTED_LENGTH } from '@/lib/utils/sv-nit'

type BaseTextInputProps = ComponentProps<typeof TextInput>

type StructuredTextInputProps = Omit<BaseTextInputProps, 'onChangeText' | 'value'> & {
  value: string
  onChangeText: (value: string) => void
  format?: (value: string) => string
}

export const StructuredTextInput = forwardRef<
  ComponentRef<typeof TextInput>,
  StructuredTextInputProps
>(function StructuredTextInput({ value, onChangeText, format, ...props }, ref) {
  const handleChange = useCallback(
    (next: string) => {
      onChangeText(format ? format(next) : next)
    },
    [format, onChangeText],
  )

  return <TextInput ref={ref} value={value} onChangeText={handleChange} {...props} />
})

export const PersonNameInput = forwardRef<
  ComponentRef<typeof TextInput>,
  Omit<StructuredTextInputProps, 'format'>
>(function PersonNameInput(props, ref) {
  return (
    <StructuredTextInput
      ref={ref}
      autoCapitalize="words"
      autoCorrect={false}
      textContentType="name"
      {...props}
    />
  )
})

export const EmailInput = forwardRef<
  ComponentRef<typeof TextInput>,
  Omit<StructuredTextInputProps, 'format'>
>(function EmailInput(props, ref) {
  return (
    <StructuredTextInput
      ref={ref}
      format={(value) => value.trim().toLowerCase()}
      keyboardType="email-address"
      inputMode="email"
      autoCapitalize="none"
      autoCorrect={false}
      autoComplete="email"
      textContentType="emailAddress"
      {...props}
    />
  )
})

export const PasswordInput = forwardRef<
  ComponentRef<typeof TextInput>,
  Omit<StructuredTextInputProps, 'format'>
>(function PasswordInput(props, ref) {
  return (
    <StructuredTextInput
      ref={ref}
      secureTextEntry
      autoCapitalize="none"
      autoCorrect={false}
      textContentType="password"
      {...props}
    />
  )
})

export const SvDuiInput = forwardRef<
  ComponentRef<typeof TextInput>,
  Omit<StructuredTextInputProps, 'format'>
>(function SvDuiInput(props, ref) {
  return (
    <StructuredTextInput
      ref={ref}
      format={formatSvDui}
      keyboardType="number-pad"
      inputMode="numeric"
      autoCorrect={false}
      autoCapitalize="none"
      {...props}
    />
  )
})

export const IsoDateInput = forwardRef<
  ComponentRef<typeof TextInput>,
  Omit<StructuredTextInputProps, 'format'>
>(function IsoDateInput(props, ref) {
  return (
    <StructuredTextInput
      ref={ref}
      format={formatIsoDateInput}
      keyboardType="number-pad"
      inputMode="numeric"
      autoCorrect={false}
      autoCapitalize="none"
      {...props}
    />
  )
})

export const SvNitInput = forwardRef<
  ComponentRef<typeof TextInput>,
  Omit<StructuredTextInputProps, 'format'>
>(function SvNitInput(props, ref) {
  return (
    <StructuredTextInput
      ref={ref}
      format={formatSvNit}
      keyboardType="number-pad"
      inputMode="numeric"
      maxLength={SV_NIT_FORMATTED_LENGTH}
      autoCorrect={false}
      autoCapitalize="none"
      {...props}
    />
  )
})

export const LegalEntityNameInput = forwardRef<
  ComponentRef<typeof TextInput>,
  Omit<StructuredTextInputProps, 'format'>
>(function LegalEntityNameInput(props, ref) {
  return (
    <StructuredTextInput
      ref={ref}
      format={normalizeLegalEntityName}
      autoCapitalize="characters"
      autoCorrect={false}
      {...props}
    />
  )
})
